import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'

import type { GetShippingResponse } from '~/api/product-page/types'
import { formatDeliveryDateRange } from '~/scopes/shipping/utils/deliveryDate.utils'

import { useDeliveryDateAdjustment } from '../useDeliveryDateAdjustement/useDeliveryDateAdjustement'

export function useDeliveryDatesFromShipping(
  shipping?: MaybeRefOrGetter<
    | GetShippingResponse
    | NonNullable<GetPickersResponse['selectedOffer']>['shipping']
    | null
  >,
) {
  const i18n = useI18n()
  const currentLocale = useI18nLocale()
  const deliveryDateAdjustment = useDeliveryDateAdjustment()

  return computed(() => {
    const value = toValue(shipping)

    const free = value?.free ?? null
    const express = value?.express ?? null

    const hasExpressShipping =
      express !== null &&
      express.price !== null &&
      Number(express.price?.amount) > 0

    const freeEarliestDate = free
      ? deliveryDateAdjustment(free.earliestArrivalDate)
      : null
    const freeLatestDate = free ? new Date(free.latestArrivalDate) : null

    const expressEarliestDate = express
      ? deliveryDateAdjustment(express.earliestArrivalDate)
      : null
    const expressLatestDate = express
      ? new Date(express.latestArrivalDate)
      : null

    return {
      free:
        freeEarliestDate && freeLatestDate
          ? formatDeliveryDateRange(
              freeEarliestDate,
              freeLatestDate,
              currentLocale,
            )
          : null,
      express:
        hasExpressShipping &&
        expressEarliestDate &&
        expressLatestDate &&
        express?.price
          ? {
              ...formatDeliveryDateRange(
                expressEarliestDate,
                expressLatestDate,
                currentLocale,
              ),
              amount: i18n.price(express.price),
            }
          : null,
    }
  })
}
